/* ==========================================================================
   Gifting options
   --------------------------------------------------------------------------
   One selectable drawer, owned by the global `gifting-options-drawer` section
   so it exists on every template. It is opened from two places and swaps its
   contents to match:

   1. the Rebuy cart bar ("Add gifting options?") - lists the gifting products
      of whatever is currently in the cart, rendered in Liquid from
      `product.metafields.custom.add_on_gifting_products` and re-rendered
      through the Section Rendering API when the set of regular cart products
      changes.

   2. the PDP "Gifting options available - Learn more" link - lists that one
      product's gifting options, cloned in from the `<template>` the
      `add-on-product` block ships next to its trigger.

   Either way, Apply diffs the selection against the cart and adds/removes real
   line items flagged with `_gifting_extra_product`. Gift lines stay visible in
   the cart. The gift note is stored as a cart attribute so it reaches the
   order.
   ========================================================================== */

(function () {
  if (window.__giftingOptionsInit) return;
  window.__giftingOptionsInit = true;

  var PROP_EXTRA = '_gifting_extra_product';

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Where the bar goes in the Rebuy cart, best first. The footer wrapper holds
  // the discount form, the subtotal and the checkout button, so "afterbegin"
  // puts the bar at the top of that block - directly above the discount field.
  var BUILT_IN_BAR_TARGETS = [
    { selector: '[data-rebuy-cart-anchor="footer"]', position: 'afterbegin' },
    { selector: '[data-rebuy-component="discount-input"]', position: 'beforebegin' },
    { selector: '.rebuy-cart__discount-container', position: 'beforebegin' },
  ];

  function log() {
    if (!window.__giftingOptionsDebug) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[gifting]');
    console.log.apply(console, args);
  }

  /* ------------------------------------------------------------- cart api */

  function getCart() {
    return fetch(root + 'cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then(function (r) {
      return r.json();
    });
  }

  function addItems(items) {
    if (!items.length) return Promise.resolve(null);
    return fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: items }),
    }).then(function (r) {
      return r.json();
    });
  }

  // /cart/change.js only takes one line at a time, so removals are chained.
  function removeKeys(keys) {
    return keys.reduce(function (chain, key) {
      return chain.then(function () {
        return fetch(root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: key, quantity: 0 }),
        });
      });
    }, Promise.resolve());
  }

  function setAttributes(attributes) {
    return fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: attributes }),
    }).then(function (r) {
      return r.json();
    });
  }

  function isGiftLine(item) {
    return !!(item && item.properties && String(item.properties[PROP_EXTRA]) === 'true');
  }

  /* ------------------------------------------------------------ rebuy glue */

  function refreshRebuy() {
    var smartcart =
      (window.Rebuy && (window.Rebuy.SmartCart || window.Rebuy.smartcart)) || null;

    if (smartcart) {
      var methods = ['updateCart', 'refreshCart', 'reloadCart', 'getCart', 'update', 'refresh'];
      for (var i = 0; i < methods.length; i++) {
        if (typeof smartcart[methods[i]] === 'function') {
          try {
            smartcart[methods[i]]();
            log('refreshed Rebuy via', methods[i]);
            return;
          } catch (e) {
            log('Rebuy refresh failed via', methods[i], e);
          }
        }
      }
    }

    // Fallback: themes/apps listening for a generic cart update still react.
    document.dispatchEvent(new CustomEvent('rebuy:cart.change', {
      bubbles: true,
      detail: { source: 'gifting-options' },
    }));
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  /* ------------------------------------------------------ custom element */

  function defineElement() {
    if (customElements.get('gifting-options')) return;

    customElements.define(
      'gifting-options',
      class extends HTMLElement {
        connectedCallback() {
          if (this.__ready) return;
          this.__ready = true;

          if (this.__initialized) {
            window.GiftingOptions = this;
            window.addEventListener('resize', this.__resizeHandler);
            document.addEventListener('click', this.__triggerClick);
            this.cartEventNames.forEach(function (name) {
              document.addEventListener(name, this.__cartEvent);
            }, this);
            if (this.currentCart && this.attachRebuyObserver()) {
              this.scheduleCartRefresh(this.currentCart);
            }
            return;
          }
          this.__initialized = true;

          this.sectionId = this.dataset.sectionId;
          this.strings = this.parseStrings();
          this.noteKey = this.dataset.noteKey || 'Gift note';

          this.drawer = this.querySelector('[data-gifting-drawer]');
          this.cartItems = this.querySelector('[data-gifting-items="cart"]');
          this.customItems = this.querySelector('[data-gifting-items="custom"]');
          this.cartText = this.querySelector('[data-gifting-cart-text]');
          this.titleEl = this.querySelector('[data-gifting-title]');
          this.noteInput = this.querySelector('[data-gifting-note]');
          this.noteCount = this.querySelector('[data-gifting-note-count]');
          this.barTemplate = this.querySelector('[data-gifting-bar-template]');

          this.defaultTitle = this.titleEl ? this.titleEl.textContent.trim() : '';

          this.enableCartBar = this.dataset.enableCartBar !== 'false';
          this.cartBarPosition = this.dataset.cartBarPosition || 'afterbegin';
          this.cartBarTargets = this.parseBarTargets();

          this.bar = null;
          this.source = 'cart';
          this.state = { active: false, summary: '' };
          this.cartEventNames = ['rebuy:smartcart.show', 'rebuy:cart.change'];
          this.__pendingCart = null;
          this.__refreshFrame = null;
          this.__rebuyObserver = null;
          this.__rebuyRoot = null;
          this.__rebuyAttachTimer = null;
          this.__rebuyAttachAttempts = 0;
          this.__refreshPromise = null;
          this.__refreshAgain = false;
          this.__needsCartFetch = false;
          this.currentCart = null;

          window.GiftingOptions = this;

          this.bindDrawer();
          this.bindNote();
          this.bindTriggers();
          this.bindCartEvents();
          this.measureText();
        }

        disconnectedCallback() {
          document.removeEventListener('click', this.__triggerClick);
          this.cartEventNames.forEach(function (name) {
            document.removeEventListener(name, this.__cartEvent);
          }, this);
          window.removeEventListener('resize', this.__resizeHandler);

          if (this.__refreshFrame !== null) cancelAnimationFrame(this.__refreshFrame);
          if (this.__resizeTimer) clearTimeout(this.__resizeTimer);
          if (this.__mountTimer) clearTimeout(this.__mountTimer);
          if (this.__rebuyAttachTimer) clearTimeout(this.__rebuyAttachTimer);
          if (this.__rebuyObserver) this.__rebuyObserver.disconnect();
          if (this.bar && this.bar.isConnected) this.bar.remove();
          if (window.GiftingOptions === this) delete window.GiftingOptions;

          this.__rebuyObserver = null;
          this.__rebuyRoot = null;
          this.__rebuyAttachTimer = null;
          this.__rebuyAttachAttempts = 0;
          this.__ready = false;
        }

        parseStrings() {
          try {
            return JSON.parse(this.dataset.strings || '{}');
          } catch (e) {
            return {};
          }
        }

        // "selector @ position | selector @ position". Each candidate carries
        // its own position because "inside at the top" is only right for the
        // footer wrapper - matching the discount block itself needs
        // "beforebegin" to land in the same place.
        //
        // BUILT_IN_BAR_TARGETS are tried first and the section setting is
        // appended after them, so a selector saved in the theme editor before
        // this default changed can't drag the bar back out of the footer.
        parseBarTargets() {
          var fallback = this.cartBarPosition;

          var configured = (this.dataset.cartBarSelector || '')
            .split('|')
            .map(function (entry) {
              var parts = entry.split('@');
              var selector = parts[0].trim();
              if (!selector) return null;
              return {
                selector: selector,
                position: (parts[1] || '').trim() || fallback,
              };
            })
            .filter(Boolean);

          return BUILT_IN_BAR_TARGETS.concat(configured);
        }

        /* -------------------------------------------------------- drawer */

        bindDrawer() {
          var self = this;
          var drawer = this.drawer;
          if (!drawer) return;

          drawer.querySelectorAll('[data-gifting-close]').forEach(function (button) {
            button.addEventListener('click', function (event) {
              event.preventDefault();
              self.closeDrawer();
            });
          });

          // Clicking the scrim (the dialog itself, not the panel) closes it.
          drawer.addEventListener('click', function (event) {
            if (event.target === drawer) self.closeDrawer();
          });

          drawer.addEventListener('keydown', function (event) {
            if (event.code === 'Escape') {
              event.preventDefault();
              self.closeDrawer();
            }
          });

          drawer.addEventListener('click', function (event) {
            var more = event.target.closest('[data-gifting-more]');
            if (more) {
              event.preventDefault();
              self.toggleText(more);
              return;
            }

            var item = event.target.closest('[data-gifting-item]');
            if (item) self.toggleItem(item);
          });

          // The rows are role="checkbox" divs so the "Read more" button can
          // legally live inside them, which means space/enter is on us.
          drawer.addEventListener('keydown', function (event) {
            if (event.code !== 'Space' && event.code !== 'Enter') return;
            if (event.target.closest('[data-gifting-more]')) return;

            var item = event.target.closest('[data-gifting-item]');
            if (!item) return;

            event.preventDefault();
            self.toggleItem(item);
          });

          var apply = drawer.querySelector('[data-gifting-apply]');
          var reset = drawer.querySelector('[data-gifting-reset]');

          if (apply) apply.addEventListener('click', function () { self.apply(); });
          if (reset) reset.addEventListener('click', function () { self.reset(); });

          // A resize can turn a clamped description into a fitting one.
          this.__resizeHandler = function () {
            clearTimeout(self.__resizeTimer);
            self.__resizeTimer = setTimeout(function () {
              self.measureText();
            }, 200);
          };
          window.addEventListener('resize', this.__resizeHandler);
        }

        toggleItem(item) {
          if (item.getAttribute('aria-disabled') === 'true') return;
          item.setAttribute(
            'aria-checked',
            (item.getAttribute('aria-checked') !== 'true').toString()
          );
        }

        openDrawer() {
          if (!this.drawer) return;
          this.drawer.setAttribute('open', '');
          this.drawer.removeAttribute('inert');
          this.drawer.setAttribute('aria-hidden', 'false');
          document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));
          var focusable = this.drawer.querySelector('[data-gifting-close]');
          if (focusable) focusable.focus();
        }

        closeDrawer() {
          if (!this.drawer) return;
          this.drawer.removeAttribute('open');
          this.drawer.setAttribute('inert', '');
          this.drawer.setAttribute('aria-hidden', 'true');
          document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
        }

        /* ----------------------------------------------------- read more */

        // A description only gets a toggle when it actually overflows its
        // clamp, so short copy keeps a clean row.
        measureText() {
          var self = this;

          this.querySelectorAll('[data-gifting-text]').forEach(function (text) {
            var more = text.parentElement.querySelector('[data-gifting-more]');
            if (!more) return;

            if (text.classList.contains('is-expanded')) {
              more.hidden = false;
              return;
            }

            var overflows = text.scrollHeight - text.clientHeight > 1;
            more.hidden = !overflows;
            more.textContent = self.strings.read_more || 'Read more';
          });
        }

        toggleText(button) {
          var text = button.parentElement.querySelector('[data-gifting-text]');
          if (!text) return;

          var expanded = text.classList.toggle('is-expanded');
          button.textContent = expanded
            ? this.strings.read_less || 'Read less'
            : this.strings.read_more || 'Read more';
        }

        /* ------------------------------------------------------ triggers */

        bindTriggers() {
          var self = this;

          this.__triggerClick = function (event) {
            var trigger = event.target.closest('[data-gifting-open]');
            if (!trigger) return;

            event.preventDefault();
            self.open(trigger);
          };
          document.addEventListener('click', this.__triggerClick);
        }

        // The PDP block owns its copy and its product list, so pull both off
        // the trigger's scope and paint them in before opening. Anything else
        // (the cart bar) falls back to the cart-derived list.
        open(trigger) {
          var scope = trigger && trigger.closest('[data-gifting-trigger-scope]');
          var template = scope && scope.querySelector('[data-gifting-panel-template]');

          if (template && this.customItems) {
            this.customItems.innerHTML = '';
            this.customItems.appendChild(template.content.cloneNode(true));
            this.showSource('custom');

            if (this.titleEl && trigger.dataset.giftingTitle) {
              this.titleEl.textContent = trigger.dataset.giftingTitle;
            }
          } else {
            this.showSource('cart');
            if (this.titleEl) this.titleEl.textContent = this.defaultTitle;
          }

          this.openDrawer();
          // Reflect what is already in the cart on the freshly painted list.
          this.syncFromCart(trigger ? null : this.currentCart).then(this.measureText.bind(this));
        }

        showSource(source) {
          this.source = source;

          if (this.cartItems) this.cartItems.hidden = source !== 'cart';
          if (this.customItems) this.customItems.hidden = source !== 'custom';
          if (this.cartText) this.cartText.hidden = source !== 'cart';
        }

        /* --------------------------------------------------------- cart */

        bindCartEvents() {
          var self = this;
          this.__cartEvent = function (event) {
            if (event.detail && event.detail.source === 'gifting-options') return;

            var cart =
              (event.detail && event.detail.smartcart && event.detail.smartcart.cart) ||
              (event.detail && event.detail.cart) ||
              null;

            if (!self.attachRebuyObserver()) self.waitForRebuyRoot();
            self.scheduleCartRefresh(cart);
          };

          this.cartEventNames.forEach(function (name) {
            document.addEventListener(name, self.__cartEvent);
          });

        }

        findRebuyRoot() {
          return document.querySelector(
            '#rebuy-cart, .rebuy-cart, [data-rebuy-component="smart-cart"]'
          );
        }

        // Rebuy can dispatch ready/show just before Vue inserts the cart root.
        // Retry for a short, bounded period after those events instead of
        // observing the entire document for the lifetime of the page.
        waitForRebuyRoot() {
          if (this.__rebuyObserver || this.__rebuyAttachTimer) return;
          if (this.__rebuyAttachAttempts >= 20) {
            this.__rebuyAttachAttempts = 0;
            return;
          }

          var self = this;
          this.__rebuyAttachTimer = setTimeout(function () {
            self.__rebuyAttachTimer = null;
            if (!self.isConnected) return;

            if (self.attachRebuyObserver()) {
              self.__rebuyAttachAttempts = 0;
              self.scheduleMount(0);
              return;
            }

            self.__rebuyAttachAttempts += 1;
            self.waitForRebuyRoot();
          }, 50);
        }

        // Rebuy replaces parts of its own cart DOM. Observing only that root
        // keeps the gifting bar mounted without processing unrelated mutations
        // from reviews, recommendations, wishlists, or the rest of the page.
        attachRebuyObserver() {
          var self = this;
          var rebuyRoot = this.findRebuyRoot();
          if (!rebuyRoot) return false;
          if (this.__rebuyRoot === rebuyRoot && this.__rebuyObserver) return true;

          if (this.__rebuyObserver) this.__rebuyObserver.disconnect();
          this.__rebuyRoot = rebuyRoot;
          this.__rebuyObserver = new MutationObserver(function () {
            self.scheduleMount(0);
          });
          this.__rebuyObserver.observe(rebuyRoot, { childList: true, subtree: true });
          return true;
        }

        scheduleCartRefresh(cart) {
          if (cart) this.__pendingCart = cart;
          if (this.__refreshFrame !== null) return;

          var self = this;
          this.__refreshFrame = requestAnimationFrame(function () {
            self.__refreshFrame = null;
            if (!self.isConnected) return;

            var pendingCart = self.__pendingCart;
            self.__pendingCart = null;
            self.refreshFromCart(pendingCart);
          });
        }

        getCartSignature(cart) {
          var seen = {};
          return (cart.items || [])
            .filter(function (item) {
              var productId = String(item.product_id || '');
              if (isGiftLine(item) || !productId || seen[productId]) return false;
              seen[productId] = true;
              return true;
            })
            .map(function (item) {
              return String(item.product_id);
            })
            .join(',');
        }

        // The server-rendered gift options only depend on which regular
        // products are in the cart. Re-render the section when that set changes;
        // quantity changes, opening the cart, and gift-line updates only need a
        // cheap local state sync.
        refreshFromCart(cart) {
          var self = this;
          if (cart) {
            this.__pendingCart = cart;
            this.__needsCartFetch = false;
          } else if (!this.__pendingCart) {
            this.__needsCartFetch = true;
          }

          if (this.__refreshPromise) {
            this.__refreshAgain = true;
            return this.__refreshPromise;
          }

          var nextCart = this.__pendingCart;
          this.__pendingCart = null;
          this.__needsCartFetch = false;
          var cartPromise = nextCart ? Promise.resolve(nextCart) : getCart();

          var refresh = cartPromise
            .then(function (currentCart) {
              if (!self.isConnected) return currentCart;

              var signature = self.getCartSignature(currentCart);
              var render = signature === (self.dataset.cartSignature || '')
                ? Promise.resolve()
                : self.renderSection();

              return render.then(function () {
                return self.syncFromCart(currentCart);
              });
            })
            .catch(function (error) {
              log('cart refresh failed', error);
            });

          this.__refreshPromise = refresh.finally(function () {
            self.__refreshPromise = null;
            if (!self.isConnected) return;
            if (!self.__refreshAgain && !self.__pendingCart && !self.__needsCartFetch) return;

            self.__refreshAgain = false;
            return self.refreshFromCart(self.__pendingCart);
          });

          return this.__refreshPromise;
        }

        // Re-render this section server side so the drawer lists the gifting
        // products of whatever is currently in the cart.
        renderSection() {
          var self = this;
          if (!this.sectionId) return Promise.resolve();

          return fetch(root + '?sections=' + encodeURIComponent(this.sectionId), {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
          })
            .then(function (r) {
              return r.json();
            })
            .then(function (data) {
              if (!self.isConnected) return;

              var html = data[self.sectionId];
              if (!html) return;

              var parsed = new DOMParser().parseFromString(html, 'text/html');
              var fresh = parsed.querySelector('gifting-options');
              var source = fresh && fresh.querySelector('[data-gifting-items="cart"]');

              if (self.cartItems && source) {
                self.cartItems.innerHTML = source.innerHTML;
                self.measureText();
              }
              if (fresh) {
                self.dataset.hasGifts = fresh.dataset.hasGifts || 'false';
                self.dataset.cartSignature = fresh.dataset.cartSignature || '';
              }
            })
            .catch(function (error) {
              log('section render failed', error);
            });
        }

        // Only the rows the customer is currently looking at, so applying from
        // a product page never touches gifts chosen elsewhere.
        get items() {
          var container = this.source === 'custom' ? this.customItems : this.cartItems;
          if (!container) return [];
          return Array.prototype.slice.call(container.querySelectorAll('[data-gifting-item]'));
        }

        get selectedItems() {
          return this.items.filter(function (item) {
            return item.getAttribute('aria-checked') === 'true';
          });
        }

        // Mirror the cart into the drawer selection, the note and the bar, so
        // every surface agrees after any cart mutation.
        syncFromCart(cart) {
          var self = this;
          var cartPromise = cart ? Promise.resolve(cart) : getCart();

          return cartPromise
            .then(function (cart) {
              self.currentCart = cart;
              var giftLines = (cart.items || []).filter(isGiftLine);

              var variantIds = giftLines.map(function (line) {
                return String(line.variant_id);
              });

              self.items.forEach(function (item) {
                item.setAttribute(
                  'aria-checked',
                  (variantIds.indexOf(String(item.dataset.variantId)) !== -1).toString()
                );
              });

              // Don't clobber a note the customer is part way through typing.
              if (self.noteInput && document.activeElement !== self.noteInput) {
                var attributes = cart.attributes || {};
                self.noteInput.value = attributes[self.noteKey] || '';
              }
              self.updateNoteCount();

              self.state.active = giftLines.length > 0;
              self.state.summary = giftLines
                .map(function (line) {
                  return line.product_title || line.title;
                })
                .join(', ');

              self.mountBar();
              self.updateBar();

              return cart;
            })
            .catch(function (error) {
              log('cart sync failed', error);
            });
        }

        /* --------------------------------------------------------- note */

        bindNote() {
          if (!this.noteInput) return;
          var self = this;
          this.noteInput.addEventListener('input', function () {
            self.updateNoteCount();
          });
          this.updateNoteCount();
        }

        updateNoteCount() {
          if (!this.noteInput || !this.noteCount) return;
          var max = parseInt(this.noteInput.getAttribute('maxlength'), 10);
          if (!max) {
            this.noteCount.textContent = '';
            return;
          }
          this.noteCount.textContent = this.noteInput.value.length + '/' + max;
        }

        saveNote(value) {
          if (!this.noteInput) return Promise.resolve(null);

          var note = typeof value === 'string' ? value : this.noteInput.value.trim();
          var attributes = {};
          attributes[this.noteKey] = note;

          return setAttributes(attributes).catch(function (error) {
            log('note save failed', error);
          });
        }

        /* ------------------------------------------------------ cart bar */

        mountBar() {
          if (!this.enableCartBar || !this.barTemplate) return;
          if (this.dataset.hasGifts !== 'true') {
            if (this.bar && this.bar.isConnected) this.bar.remove();
            this.bar = null;
            return;
          }

          var target = this.findBarTarget();
          if (!target) return;

          // Being connected isn't enough - the footer wrapper only appears once
          // Rebuy renders it, so a bar mounted early can be sitting at a
          // fallback spot. Re-check the placement and move it when it's wrong.
          if (this.bar && this.bar.isConnected && this.isBarPlaced(target)) return;

          // Rebuy's cart is Vue-rendered, so a patch can drop the bar right
          // after we add it. Re-mounting is how it heals, but rate-limit it so
          // a hostile re-render can't turn into a per-frame loop. The work is
          // deferred rather than dropped: the DOM change that revealed a better
          // target may be the last one for a while, so a swallowed mount would
          // otherwise leave the bar stranded at a fallback spot.
          var now = Date.now();
          var since = this.__lastMount ? now - this.__lastMount : Infinity;
          if (since < 250) {
            this.scheduleMount(250 - since);
            return;
          }
          this.__lastMount = now;

          // Moving the existing node keeps its click listener, so only build a
          // fresh bar the first time.
          var bar = this.bar;

          if (!bar) {
            var fragment = this.barTemplate.content.cloneNode(true);
            bar = fragment.querySelector('[data-gifting-bar]');
            if (!bar) return;

            var self = this;
            bar.addEventListener('click', function (event) {
              event.preventDefault();
              self.open(null);
            });
            bar.addEventListener('keydown', function (event) {
              if (event.code !== 'Enter' && event.code !== 'Space') return;
              event.preventDefault();
              self.open(null);
            });
          }

          target.host.insertAdjacentElement(target.position, bar);
          this.bar = bar;
          log('bar mounted', target.selector, target.position);

          this.updateBar();
        }

        scheduleMount(delay) {
          var self = this;
          if (this.__mountTimer) return;
          this.__mountTimer = setTimeout(function () {
            self.__mountTimer = null;
            self.mountBar();
          }, delay);
        }

        // First candidate present wins, so the bar sits at the top of the Rebuy
        // footer - above the discount code form - and still lands somewhere
        // sensible if Rebuy changes that wrapper.
        findBarTarget() {
          for (var i = 0; i < this.cartBarTargets.length; i++) {
            var candidate = this.cartBarTargets[i];
            var host = null;

            try {
              host = document.querySelector(candidate.selector);
            } catch (e) {
              log('bad cart bar selector', candidate.selector);
              continue;
            }

            if (host && host !== this.bar) {
              return {
                host: host,
                position: candidate.position || this.cartBarPosition,
                selector: candidate.selector,
              };
            }
          }

          return null;
        }

        isBarPlaced(target) {
          switch (target.position) {
            case 'afterbegin':
              return target.host.firstElementChild === this.bar;
            case 'beforeend':
              return target.host.lastElementChild === this.bar;
            case 'beforebegin':
              return target.host.previousElementSibling === this.bar;
            case 'afterend':
              return target.host.nextElementSibling === this.bar;
            default:
              return false;
          }
        }

        updateBar() {
          if (!this.bar) return;

          var titleEl = this.bar.querySelector('[data-gifting-bar-title]');
          var addedEl = this.bar.querySelector('[data-gifting-bar-added]');
          var inputEl = this.bar.querySelector('[data-gifting-bar-input]');

          this.bar.dataset.active = this.state.active ? 'true' : 'false';
          if (inputEl) inputEl.checked = this.state.active;

          if (titleEl) {
            titleEl.textContent = this.state.active
              ? this.strings.added_title || 'Gift Wrapping Added!'
              : this.strings.bar_title || 'Add gifting options?';
          }

          if (addedEl) {
            if (this.state.active && this.state.summary) {
              addedEl.innerHTML = '';
              addedEl.appendChild(document.createTextNode(this.state.summary));
              var edit = document.createElement('span');
              edit.className = 'gft-Bar__edit';
              edit.textContent = this.strings.edit || 'Edit';
              addedEl.appendChild(edit);
            } else {
              addedEl.textContent = '';
            }
          }
        }

        /* ---------------------------------------------------- mutations */

        setBusy(name, busy) {
          if (!this.drawer) return;
          var button = this.drawer.querySelector('[data-gifting-' + name + ']');
          if (button) button.setAttribute('aria-busy', busy ? 'true' : 'false');
        }

        // Diff the visible rows against the gift lines already in the cart,
        // then remove what was deselected and add what is new. Gift lines that
        // aren't offered by the rows on screen are left alone, so applying from
        // a product page can't wipe out a gift added from another product.
        apply() {
          var self = this;
          var visible = this.items;

          this.setBusy('apply', true);

          var visibleIds = visible.map(function (item) {
            return String(item.dataset.variantId);
          });

          var wanted = visible
            .filter(function (item) {
              return item.getAttribute('aria-checked') === 'true';
            })
            .map(function (item) {
              return {
                id: String(item.dataset.variantId),
                quantity: parseInt(item.dataset.quantity, 10) || 1,
              };
            });

          var wantedIds = wanted.map(function (item) {
            return item.id;
          });

          return getCart()
            .then(function (cart) {
              var current = (cart.items || []).filter(isGiftLine);

              var toRemove = current
                .filter(function (line) {
                  var id = String(line.variant_id);
                  return visibleIds.indexOf(id) !== -1 && wantedIds.indexOf(id) === -1;
                })
                .map(function (line) {
                  return line.key;
                });

              var currentIds = current.map(function (line) {
                return String(line.variant_id);
              });

              var toAdd = wanted
                .filter(function (item) {
                  return currentIds.indexOf(item.id) === -1;
                })
                .map(function (item) {
                  var properties = {};
                  properties[PROP_EXTRA] = 'true';
                  return { id: item.id, quantity: item.quantity, properties: properties };
                });

              log('apply', { toRemove: toRemove, toAdd: toAdd });

              return removeKeys(toRemove).then(function () {
                return addItems(toAdd);
              });
            })
            .then(function () {
              return self.saveNote();
            })
            .then(function () {
              return self.finish('apply');
            })
            .catch(function (error) {
              log('apply failed', error);
              self.setBusy('apply', false);
            });
        }

        reset() {
          var self = this;
          var visibleIds = this.items.map(function (item) {
            return String(item.dataset.variantId);
          });

          this.setBusy('reset', true);

          return getCart()
            .then(function (cart) {
              var keys = (cart.items || [])
                .filter(function (line) {
                  return isGiftLine(line) && visibleIds.indexOf(String(line.variant_id)) !== -1;
                })
                .map(function (line) {
                  return line.key;
                });
              return removeKeys(keys);
            })
            .then(function () {
              self.items.forEach(function (item) {
                item.setAttribute('aria-checked', 'false');
              });
              if (self.noteInput) self.noteInput.value = '';
              self.updateNoteCount();
              return self.saveNote('');
            })
            .then(function () {
              return self.finish('reset');
            })
            .catch(function (error) {
              log('reset failed', error);
              self.setBusy('reset', false);
            });
        }

        finish(busyName) {
          var self = this;
          refreshRebuy();
          return getCart()
            .then(function (cart) {
              return self.refreshFromCart(cart);
            })
            .then(function () {
              self.setBusy(busyName, false);
              self.closeDrawer();
            });
        }
      }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', defineElement);
  } else {
    defineElement();
  }
})();
